import { useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import { fetchDataBuilderResults } from '../../api/data-builder-api';
import type { DataBuilderPayload } from '../../api/data-builder-api.types';
import {
  saveDataBuilderDataset,
  updateDataBuilderDataset,
} from '../../api/save-dataset-api';
import { useBuildDataBuilderPayload } from '../../api/useBuildDataBuilderPayload';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import {
  dataBuilderStore,
  selectCompanyMapperGateStatus,
  selectDeliverableClient,
  selectDeliverableName,
  selectLoadedSavedDatasetId,
  useDataBuilderSelector,
} from '../../data-builder.store';
import { submitDeliverableWithLogging } from './submit-deliverable';
import { useNavigateToDelivery } from './useNavigateToDelivery';

export type SaveStatus =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; error: unknown };

type UseSaveDatasetResult = {
  handleSaveAndSubmit: () => void;
  handleUpdateAndSubmit: () => void;
  handleCreateNewAndSubmit: () => void;
  saveStatus: SaveStatus;
  canSaveAndSubmit: boolean;
  isEditingLoadedSavedSet: boolean;
};

export const useSaveDataset = (options?: {
  onVerifyComplete?: () => void;
}): UseSaveDatasetResult => {
  const companySelectionGateStatus = useDataBuilderSelector(
    selectCompanyMapperGateStatus
  );
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const finishedNavigateToDelivery = useNavigateToDelivery();
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useTanstackQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });
  const { kibanaLogger } = useKibanaLogger();

  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);
  const loadedSavedDatasetId = useDataBuilderSelector(
    selectLoadedSavedDatasetId
  );

  const payloadClient = isRevelioAdmin ? storedClient || null : null;
  const payloadName = storedName?.trim() ?? '';

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
  });

  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ status: 'idle' });

  const buildSavePayload = (): DataBuilderPayload | null => {
    if (rawSchema === undefined) {
      return null;
    }

    const payloadResult = build(rawSchema);
    if (payloadResult === null) {
      setSaveStatus({
        status: 'error',
        error: new Error('Failed to build payload'),
      });
      return null;
    }

    if (payloadName.length === 0) {
      setSaveStatus({
        status: 'error',
        error: new Error('Name is required'),
      });
      return null;
    }

    return payloadResult.payload;
  };

  const runSaveThenSubmit = (
    payload: DataBuilderPayload,
    saveAction: () => Promise<unknown>
  ) => {
    setSaveStatus({ status: 'pending' });
    void (async () => {
      try {
        await saveAction();
      } catch (error: unknown) {
        kibanaLogger?.error('Data Builder: save dataset failed');
        setSaveStatus({ status: 'error', error });
        return;
      }
      try {
        await submitDeliverableWithLogging(
          payload,
          kibanaLogger,
          'DataBuilder.useSaveDataset'
        );
        setSaveStatus({ status: 'success' });
        options?.onVerifyComplete?.();
        await finishedNavigateToDelivery();
      } catch (error: unknown) {
        setSaveStatus({ status: 'error', error });
      }
    })();
  };

  const handleSaveAndSubmit = () => {
    if (saveStatus.status === 'pending' || saveStatus.status === 'success') {
      return;
    }
    const payload = buildSavePayload();
    if (payload === null) return;
    runSaveThenSubmit(payload, () =>
      saveDataBuilderDataset({ name: payloadName, payload })
    );
  };

  const handleUpdateAndSubmit = () => {
    if (saveStatus.status === 'pending' || saveStatus.status === 'success') {
      return;
    }
    if (loadedSavedDatasetId === null) return;
    const payload = buildSavePayload();
    if (payload === null) return;
    runSaveThenSubmit(payload, () =>
      updateDataBuilderDataset({
        id: loadedSavedDatasetId,
        name: payloadName,
        payload,
      })
    );
  };

  const handleCreateNewAndSubmit = () => {
    if (saveStatus.status === 'pending' || saveStatus.status === 'success') {
      return;
    }
    const payload = buildSavePayload();
    if (payload === null) return;
    runSaveThenSubmit(payload, async () => {
      const savedDataset = await saveDataBuilderDataset({
        name: payloadName,
        payload,
      });
      dataBuilderStore.setState({ loadedSavedDatasetId: savedDataset._id });
    });
  };

  const isEditingLoadedSavedSet = loadedSavedDatasetId !== null;
  const canSaveAndSubmit =
    saveStatus.status !== 'pending' &&
    saveStatus.status !== 'success' &&
    rawSchema !== undefined &&
    payloadName.length > 0 &&
    companySelectionGateStatus === 'idle';

  return {
    handleSaveAndSubmit,
    handleUpdateAndSubmit,
    handleCreateNewAndSubmit,
    saveStatus,
    canSaveAndSubmit,
    isEditingLoadedSavedSet,
  };
};
